import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Download,
  FileText,
  CheckCircle2,
  Package,
  MapPin,
  Truck,
  FileSpreadsheet,
  Calendar,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RdaRowsTable } from "@/pages/AdminRdaPage";

export default function ClientRdaPage() {
  const [rdas, setRdas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewRda, setViewRda] = useState(null);
  const [downloadingMonth, setDownloadingMonth] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: sum }] = await Promise.all([
        api.get("/rda-monthly"),
        api.get("/consolidado/summary").catch(() => ({ data: null })),
      ]);
      setRdas(data);
      setSummary(sum);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const downloadConsolidadoMonth = async (m) => {
    setDownloadingMonth(m.month);
    try {
      const res = await api.get(`/consolidado/download?month=${m.month}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Consolidado_SAP_RDA_${m.month.replace("-", "")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Consolidado de ${m.label} descargado`);
    } catch (e) {
      const msg =
        e.response?.data instanceof Blob
          ? await e.response.data.text().then((t) => {
              try {
                return JSON.parse(t).detail;
              } catch {
                return t;
              }
            })
          : formatApiError(e.response?.data?.detail);
      toast.error(msg || "Error al descargar consolidado");
    } finally {
      setDownloadingMonth(null);
    }
  };

  const download = async (m) => {
    try {
      const res = await api.get(`/rda-monthly/${m.mes}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RDA_${m.mes.replace("-", "")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("RDA mensual descargado");
    } catch {
      toast.error("Error al descargar");
    }
  };

  const openDetail = async (m) => {
    try {
      const { data } = await api.get(`/rda-monthly/${m.mes}`);
      setViewRda(data);
    } catch {
      toast.error("Error al cargar detalle");
    }
  };

  const totals = rdas.reduce(
    (acc, r) => {
      acc.rows += r.total_rows || 0;
      acc.delivered += r.delivered_rows || 0;
      acc.bultos += r.total_bultos || 0;
      acc.unidades += r.total_unidades || 0;
      return acc;
    },
    { rows: 0, delivered: 0, bultos: 0, unidades: 0 }
  );

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Confirmaciones"
          title="Mis Entregas (RDA)"
          description="Reportes de aceptación de entrega enviados por EXIS Pharma. Descarga el archivo Excel oficial o el consolidado SAP + RDA por mes."
        />

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KpiCard
            label="Meses con RDA"
            value={rdas.length}
            icon={<FileText className="w-4 h-4" />}
          />
          <KpiCard
            label="Filas confirmadas"
            value={totals.delivered}
            icon={<CheckCircle2 className="w-4 h-4" />}
            highlight={totals.delivered > 0}
          />
          <KpiCard
            label="Bultos entregados"
            value={totals.bultos.toLocaleString("es-CL")}
            icon={<Package className="w-4 h-4" />}
          />
          <KpiCard
            label="Unidades entregadas"
            value={totals.unidades.toLocaleString("es-CL")}
            icon={<Truck className="w-4 h-4" />}
          />
        </div>

        {/* Consolidado por mes */}
        {summary?.months?.length > 0 && (
          <div className="bg-white border border-slate-200 mb-6">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
              <div className="w-9 h-9 bg-[#D95A40] text-white flex items-center justify-center">
                <FileSpreadsheet className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display font-bold text-[#0B1B3D]">
                  Consolidado SAP + RDA por mes
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">
                  Un archivo Excel por mes con las 22 columnas de SAP + RDA
                </div>
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {summary.months.map((m) => (
                <li
                  key={m.month}
                  data-testid={`client-consolidado-month-${m.month}`}
                  className="p-4 flex items-center justify-between gap-4 hover:bg-slate-50"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-display font-bold text-[#0B1B3D]">
                        {m.label}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {m.total_rows} filas ·{" "}
                        <span className="text-emerald-700 font-semibold">
                          {m.delivered} entregadas
                        </span>{" "}
                        · <span className="text-[#D95A40]">{m.pending} pendientes</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadConsolidadoMonth(m)}
                    disabled={downloadingMonth === m.month}
                    data-testid={`client-consolidado-download-${m.month}`}
                    className="flex items-center gap-2 px-4 py-2 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider transition-colors shrink-0"
                  >
                    {downloadingMonth === m.month ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                    Descargar {m.label.split(" ")[0]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="bg-white border border-slate-200 divide-y divide-slate-100">
          {loading && (
            <div className="p-10 text-center text-slate-500">Cargando...</div>
          )}
          {!loading && rdas.length === 0 && (
            <div className="p-10 text-center">
              <Truck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="text-slate-500">
                Aún no has recibido confirmaciones de entrega.
              </div>
              <div className="text-xs text-slate-400 mt-2">
                EXIS Pharma te notificará aquí cuando tus productos sean
                entregados.
              </div>
            </div>
          )}
          {rdas.map((m) => (
            <div
              key={`${m.mes}`}
              data-testid={`client-rda-row-${m.mes}`}
              className="p-5 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="w-9 h-9 bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-5 h-5" strokeWidth={2.2} />
                    </div>
                    <div className="min-w-0">
                      <button
                        onClick={() => openDetail(m)}
                        data-testid={`client-rda-view-${m.mes}`}
                        className="font-display font-bold text-lg text-[#0B1B3D] hover:text-[#D95A40] transition-colors text-left truncate"
                      >
                        RDA {m.mes}
                      </button>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {m.upload_count} subida(s) · Última:{" "}
                        {m.latest_upload_at
                          ? new Date(m.latest_upload_at).toLocaleString("es-CL")
                          : "—"}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm">
                    <Metric label="Filas únicas" value={m.total_rows} accent={false} />
                    <Metric label="Entregadas" value={m.delivered_rows} accent />
                    <Metric
                      label="Bultos"
                      value={(m.total_bultos ?? 0).toLocaleString("es-CL")}
                    />
                    <Metric
                      label="Unidades"
                      value={(m.total_unidades ?? 0).toLocaleString("es-CL")}
                    />
                    <div className="flex items-center gap-1.5 text-xs text-slate-600">
                      <MapPin className="w-3 h-3 text-[#D95A40]" />
                      <span className="font-semibold text-[#0B1B3D]">
                        {(m.comunas || []).slice(0, 3).join(" · ")}
                      </span>
                      {(m.comunas?.length || 0) > 3 && (
                        <span className="text-slate-400">
                          +{m.comunas.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => openDetail(m)}
                    data-testid={`client-rda-detail-${m.mes}`}
                    className="flex items-center gap-2 px-4 py-2.5 border border-slate-300 hover:border-[#0B1B3D] hover:bg-slate-50 text-slate-700 text-xs font-bold uppercase tracking-wider transition-colors"
                  >
                    <FileText className="w-4 h-4" /> Detalle
                  </button>
                  <button
                    onClick={() => download(m)}
                    data-testid={`client-rda-download-${m.mes}`}
                    className="flex items-center gap-2 px-4 py-2.5 bg-[#D95A40] hover:bg-[#c94d33] text-white text-xs font-bold uppercase tracking-wider transition-colors"
                  >
                    <Download className="w-4 h-4" /> Descargar RDA
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!viewRda} onOpenChange={(v) => !v && setViewRda(null)}>
        <DialogContent className="bg-white sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tighter">
              RDA {viewRda?.mes}
            </DialogTitle>
            <div className="text-sm text-slate-600">
              {viewRda?.upload_count} subida(s) fusionada(s) ·{" "}
              {viewRda?.total_rows} filas únicas · {viewRda?.delivered_rows}{" "}
              entregadas · {(viewRda?.total_bultos ?? 0).toLocaleString("es-CL")}{" "}
              bultos
            </div>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh] border border-slate-200">
            <RdaRowsTable rows={viewRda?.rows || []} />
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function KpiCard({ label, value, icon, highlight }) {
  return (
    <div
      className={`border p-5 transition-transform hover:-translate-y-[1px] ${
        highlight
          ? "border-emerald-300 bg-emerald-50/40"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          {label}
        </div>
        <div className={highlight ? "text-emerald-600" : "text-[#D95A40]"}>
          {icon}
        </div>
      </div>
      <div className="font-display font-black text-3xl text-[#0B1B3D] tabular-nums leading-none">
        {value}
      </div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        className={`font-display font-bold text-lg tabular-nums leading-none mt-0.5 ${
          accent ? "text-emerald-700" : "text-[#0B1B3D]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
